import { Dispatch, FC, SetStateAction, useState } from "react";
import {
  IStoreQuery,
  useRemoveAllNotifications,
  useMarkNotificationsAs
} from "@novu/notification-center";
import {
  ChatBubbleBottomCenterIcon,
  ChatBubbleBottomCenterTextIcon,
  ArchiveBoxXMarkIcon
} from "@heroicons/react/24/outline";
import { Tooltip } from "@mui/material";
import { useSelector } from "react-redux";

import { useGetFeedsQuery } from "./slice/notificationSlice";
import { NotifyError } from "./notification";
import useTracking from "../analytics/hooks/useTracking";
import { RootState } from "../../../store/store";
import { Link } from "react-router-dom";
import UserRole from "@lxp-app/utils/roles";

interface HeaderProps {
  query: IStoreQuery;
  setQuery: Dispatch<SetStateAction<IStoreQuery>>;
  unreadCount?: number;
  unseenCount?: number;
  notifyErrors?: string;
  refetchCount: () => any;
  refetchNotification: () => any;
  setNotifyErrors: Dispatch<SetStateAction<Partial<NotifyError>[] | []>>;
  messageIds: string[] | undefined;
  setFeedMap: Dispatch<
    SetStateAction<{
      name: string | undefined;
      feedIdentifier: string | undefined;
    }>
  >;
}

const Header: FC<HeaderProps> = ({
  setQuery,
  query,
  messageIds,
  setNotifyErrors,
  setFeedMap,
  refetchCount,
  refetchNotification
}) => {
  const [track] = useTracking();
  const user = useSelector((state: RootState) => state.UserInfoSlice);
  const [selected, setSelected] = useState<number | undefined>(-1);
  const { isSuccess, data } = useGetFeedsQuery(undefined);
  const [feedsList, _] = useState(["6555ac75830a4c0b23fecf0e"]);
  const isManager = user?.profile?.isManager;
  const isAdmin = user?.profile?.role?.includes(UserRole.ADMIN);
  const { removeAllNotifications } = useRemoveAllNotifications({
    onSuccess: () => {
      // no op
      refetchCount();
    },
    onError: function () {
      setNotifyErrors((oldErrors) => {
        if (oldErrors?.length > 0) {
          return [
            ...oldErrors,
            {
              label: "Failed!",
              severity: "error",
              messageList: [
                "Error while deleting all notifcations"
              ] as unknown as []
            }
          ];
        } else {
          return [
            {
              label: "Failed!",
              severity: "error",
              messageList: [
                "Error while deleting all notifcations"
              ] as unknown as []
            }
          ];
        }
      });
    }
  });

  const { markNotificationsAs } = useMarkNotificationsAs({
    onSuccess: () => {
      refetchCount();
      refetchNotification();
      // no op
      //   return [
      //     ...oldErrors,
      //     {
      //       label: "Success!",
      //       severity: "success",
      //       messageList: [
      //         "notifications read successfully",
      //       ] as unknown as [],
      //     },
      //   ];
      // });
    },
    onError(_error, _variables, _context) {
      setNotifyErrors((oldErrors) => {
        if (oldErrors?.length > 0) {
          return [
            ...oldErrors,
            {
              label: "Failed!",
              severity: "error",
              messageList: [
                "Error while reading all notifcations"
              ] as unknown as []
            }
          ];
        } else {
          return [
            {
              label: "Failed!",
              severity: "error",
              messageList: [
                "Error while reading all notifcations"
              ] as unknown as []
            }
          ];
        }
      });
    }
  });

  const handleTabChange = (feed: any, index: any) => {
    setSelected(index);
    setQuery((prevQuery) => {
      return { ...prevQuery, feedIdentifier: feed.identifier };
    });
    setFeedMap({
      name: feed.name,
      feedIdentifier: feed.identifier
    });
  };

  function handleReadClick() {
    setQuery({
      ...query,
      read: query.read === undefined ? false : undefined,
      seen: query.seen === undefined ? false : undefined
    });
  }

  function handleAllDelete() {
    removeAllNotifications({ feedId: "" });
    track("onClick", {
      eventName: "onClick",
      object: {
        id: user.profile.id,
        objectType: "NOTIFICATION"
      },
      verb: {
        id: "NOTIFICATION_DELETE",
        display: {
          "en-US": "Delete"
        }
      }
    });
  }

  function handleMarksAllRead() {
    markNotificationsAs(
      { messageId: messageIds ?? [], seen: true, read: true },
      {}
    );
    track("onClick", {
      eventName: "onClick",
      object: {
        id: user.profile.id,
        objectType: "NOTIFICATION"
      },
      verb: {
        id: "NOTIFICATION_READ",
        display: {
          "en-US": "Read"
        }
      }
    });
  }

  function handleMarksAllUnread() {
    markNotificationsAs({
      messageId: messageIds ?? [],
      seen: false,
      read: false
    });
    track("onClick", {
      eventName: "onClick",
      object: {
        id: user.profile.id,
        objectType: "NOTIFICATION"
      },
      verb: {
        id: "NOTIFICATION_UNREAD",
        display: {
          "en-US": "Unread"
        }
      }
    });
  }

  return (
    <>
      <div className="flex items-center gap-2 px-4 pt-8 overflow-hidden border-4-">
        <div className="text-xl font-semibold text-gray-900 dark:text-gray-50">
          Notifications
        </div>
        <div className="flex justify-end w-full gap-4 px-2">
          <Tooltip title="Read All" placement="bottom-end">
            <div
              className="p-2 rounded-lg bg-secondary/5"
              data-testid="markAllRead"
            >
              <ChatBubbleBottomCenterTextIcon
                className="w-5 aspect-square hover:cursor-pointer"
                onClick={handleMarksAllRead}
              />
            </div>
          </Tooltip>
          <Tooltip title="Unread All" placement="bottom-end">
            <div
              className="p-2 rounded-lg bg-secondary/5"
              data-testid="markAllUnread"
            >
              <ChatBubbleBottomCenterIcon
                className="w-5 aspect-square hover:cursor-pointer"
                onClick={handleMarksAllUnread}
              />
            </div>
          </Tooltip>
          <Tooltip title="Delete All" placement="bottom-end">
            <div
              className="p-2 rounded-lg bg-secondary/5"
              data-testid="deleteAll"
            >
              <ArchiveBoxXMarkIcon
                className="w-5 aspect-square hover:cursor-pointer"
                onClick={handleAllDelete}
              />
            </div>
          </Tooltip>
          {/* <CircularProgress variant="determinate" value={75} size="1.25rem"/> */}
        </div>
      </div>
      {isSuccess && (isAdmin || isManager) ? (
        <div className="flex items-center justify-between px-4 mt-3 text-xs font-semibold text-gray-900 bg-gray-50 dark:bg-gray-800 dark:text-gray-50">
          <div className="flex items-center w-full gap-3 mr-2 overflow-hidden ">
            <div
              className="flex flex-col gap-2 mt-2 hover:cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-500 dark:bg-gray-800"
              key={"feedID"}
              role="tab"
              onClick={() => {
                handleTabChange({ name: undefined }, -1);
              }}
              data-testid="tabChange"
            >
              <div className={`${selected === -1 ? "text-primary" : ""}`}>
                {" "}
                {"All".toUpperCase()}{" "}
              </div>
              <div
                className={`min-w-min h-[2px] ${
                  selected === -1 ? "bg-primary" : ""
                }`}
              />
            </div>
            {data?.data
              ?.filter((feed: any) => !feedsList.includes(feed._id))
              .map((feed: any, index: number) => (
                <div
                  className="flex flex-col gap-2 mt-2 hover:cursor-pointer"
                  key={feed._id}
                  role="tab"
                  onClick={() => {
                    handleTabChange(feed, index);
                  }}
                  data-testid={`tabChange${index}`}
                >
                  <div
                    className={` ${
                      selected === index ? "text-primary" : "text-secondary"
                    }`}
                  >
                    {" "}
                    {"Approval Requests".toUpperCase()}{" "}
                  </div>
                  <div
                    className={`min-w-min h-[2px] ${
                      selected === index ? "bg-primary" : ""
                    }`}
                  />
                </div>
              ))}
          </div>
        </div>
      ) : null}
      {/* <div className="h-[1px] w-full bg-secondary mt-1"/> 
      block truncate text-sm font-semibold leading-6 text-gray-900 dark:text-gray-50
      */}
      <div className="pl-5 mt-5 font-semibold text-primary flex justify-left">
        <Link to="/notifications">
          <span className="hover:cursor-pointer ">See All Notifications</span>
        </Link>
      </div>
      <div className="flex justify-end p-2 px-2 text-sm font-semibold text-primary min-w-max border-4-">
        <button
          className={`px-4 py-[1px] rounded-l-full w-20 ${
            query.read === undefined
              ? "bg-primary text-white "
              : "ring-1 ring-inset ring-secondary/20"
          }`}
          disabled={query.read === undefined}
          onClick={handleReadClick}
          data-testid="allViewBtn"
        >
          {" "}
          All{" "}
        </button>
        <button
          className={`px-4 py-[1px] rounded-r-full w-20 ${
            query.read === false
              ? "bg-primary text-white "
              : "ring-1 ring-inset ring-secondary/20"
          }`}
          disabled={query.read === false}
          onClick={handleReadClick}
          data-testid="unreadViewBtn"
        >
          {" "}
          Unread{" "}
        </button>
      </div>
    </>
  );
};

export default Header;
